/**
 * Sanitize raw dispatch briefs rendered in SubAgentChatView.
 *
 * Marcus dispatches Banan/Vid sub-agent work by sending a machine-generated
 * task message as the first user event on the Anthropic session. Those
 * briefs are built for the LLM — they carry tool-use framing, system-prompt
 * references, private CDN reference URLs, and the full JSON schema. They
 * are unsafe and unfriendly to show verbatim in the chat UI.
 *
 * This module walks user-role text parts and, for any part whose text
 * matches a known brief shape, replaces it with a compact, human-readable
 * markdown summary. The summary keeps the bits a user actually cares about
 * (what's being generated, for which product, in what aspect ratio/
 * duration, which script angle/hook) and drops everything else (reference
 * URLs, tool instructions, system-prompt pointers).
 *
 * All helpers are pure and return the input reference unchanged when no
 * brief was detected — callers can depend on referential equality for
 * `useMemo` stability.
 */

import type { UIMessage } from "@ai-sdk/react";

const MAX_PROMPT_CHARS = 180;
const MAX_LIST_ITEMS = 8;

// ── Public API ──────────────────────────────────────────────────────────────

export function sanitizeDispatchBrief(text: string): string {
  if (!text || text.length < 24) return text;

  // Order matters: most-specific tokens first so we don't fall through to
  // a looser matcher that accidentally eats a more complex brief.
  if (text.includes("```json") && /Generate the (?:\d+ )?(?:video|image)s? described in the JSON below/.test(text)) {
    const kling = summarizeKlingBrief(text);
    if (kling) return kling;
  }

  if (text.startsWith("# UGC Video ")) {
    const ugc = summarizeUgcSceneBrief(text);
    if (ugc) return ugc;
  }

  if (text.startsWith("# UGC Element Stills ")) {
    const stills = summarizeUgcElementStillsBrief(text);
    if (stills) return stills;
  }

  if (/^Generate \d+ images? using the generate_image tool\./.test(text)) {
    const batch = summarizeBananBatchBrief(text);
    if (batch) return batch;
  }

  if (text.startsWith("Generate an image using the generate_image tool")) {
    const single = summarizeBananSingleBrief(text);
    if (single) return single;
  }

  if (text.startsWith("# Image Ad Remix") || text.startsWith("# Image Ad Brief")) {
    const remix = summarizeImageAdBrief(text);
    if (remix) return remix;
  }

  if (text.startsWith("## Google Ads Task Brief")) {
    const gads = summarizeGadsBrief(text);
    if (gads) return gads;
  }

  return text;
}

export function sanitizeDispatchBriefParts(
  parts: UIMessage["parts"],
): UIMessage["parts"] {
  if (!parts || parts.length === 0) return parts;
  let changed = false;
  const next = parts.map((part) => {
    if (!part || (part as { type?: string }).type !== "text") return part;
    const p = part as { type: "text"; text?: string };
    if (typeof p.text !== "string") return part;
    const sanitized = sanitizeDispatchBrief(p.text);
    if (sanitized === p.text) return part;
    changed = true;
    return { ...p, text: sanitized } as UIMessage["parts"][number];
  });
  return changed ? next : parts;
}

export function sanitizeDispatchBriefMessages(
  messages: UIMessage[],
): UIMessage[] {
  if (!messages || messages.length === 0) return messages;
  let changed = false;
  const next = messages.map((msg) => {
    if (msg.role !== "user") return msg;
    const sanitizedParts = sanitizeDispatchBriefParts(msg.parts);
    if (sanitizedParts === msg.parts) return msg;
    changed = true;
    return { ...msg, parts: sanitizedParts };
  });
  return changed ? next : messages;
}

// ── Image-ad assistant result rewrite ──────────────────────────────────────
//
// When Banan is dispatched from the static-ads page, the agent's final reply
// is a chatty multi-paragraph wrap-up that re-lists every generated URL as
// plain text *and* as markdown — on top of the `[Attached media]` thumbnail
// strip the chat already renders from collected tool-result URLs. The
// duplicated URL text adds nothing the user can't already see, so swap the
// narrative for a short canned line and leave the annotation in place.

const IMAGE_AD_FINAL_TEXT =
  "Step one complete — your images are generated. Let me know how you'd like to tweak or update them — your imagination is the limit!";

const ATTACHED_MEDIA_TAIL =
  /\n*\[(?:Attached images?|Attached media)\]\n[\s\S]*$/;

/** True iff the first user message of the conversation is a static-ads
 *  ImageAdSetupModal dispatch (raw `# Image Ad Brief` / `# Image Ad Remix`)
 *  OR its sanitized summary form. Detection runs on whichever shape we're
 *  passed so the caller can chain with `sanitizeDispatchBriefMessages`. */
export function isImageAdDispatch(messages: UIMessage[]): boolean {
  for (const msg of messages) {
    if (msg.role !== "user") continue;
    for (const part of msg.parts ?? []) {
      if (!part || (part as { type?: string }).type !== "text") continue;
      const text = (part as { text?: string }).text ?? "";
      if (
        text.startsWith("# Image Ad Brief") ||
        text.startsWith("# Image Ad Remix") ||
        text.startsWith("**Image ad remix**") ||
        text.startsWith("**Image ad**")
      ) {
        return true;
      }
    }
    return false;
  }
  return false;
}

/** Rewrite the last assistant message that carries an `[Attached media]`
 *  annotation: strip the narrative + URL noise that preceded it and replace
 *  with a short canned status line. The annotation itself is preserved so
 *  the chat keeps rendering the variant thumbnails it carries. */
export function sanitizeImageAdAssistantResults(
  messages: UIMessage[],
): UIMessage[] {
  if (!messages || messages.length === 0) return messages;
  if (!isImageAdDispatch(messages)) return messages;

  // Walk back to the most recent assistant message with an attached-media
  // block — that's the post-generation turn whose narrative is redundant.
  let targetIdx = -1;
  let targetPartIdx = -1;
  for (let i = messages.length - 1; i >= 0 && targetIdx === -1; i -= 1) {
    const msg = messages[i];
    if (msg.role !== "assistant") continue;
    const parts = msg.parts ?? [];
    for (let j = parts.length - 1; j >= 0; j -= 1) {
      const part = parts[j];
      if (!part || (part as { type?: string }).type !== "text") continue;
      const text = (part as { text?: string }).text ?? "";
      if (ATTACHED_MEDIA_TAIL.test(text)) {
        targetIdx = i;
        targetPartIdx = j;
        break;
      }
    }
  }
  if (targetIdx === -1) return messages;

  const target = messages[targetIdx];
  const newParts = (target.parts ?? []).map((part, j) => {
    if (j !== targetPartIdx) return part;
    const p = part as { type: "text"; text?: string };
    const text = p.text ?? "";
    const tail = text.match(ATTACHED_MEDIA_TAIL)?.[0] ?? "";
    return { ...p, text: `${IMAGE_AD_FINAL_TEXT}${tail}` } as UIMessage["parts"][number];
  });
  const next = messages.slice();
  next[targetIdx] = { ...target, parts: newParts };
  return next;
}

// ── Shared helpers ──────────────────────────────────────────────────────────

function truncate(text: string, max = MAX_PROMPT_CHARS): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length <= max) return collapsed;
  return `${collapsed.slice(0, max - 1).trimEnd()}…`;
}

function countRefUrls(block: string): number {
  const single = (block.match(/^Reference image URL:/gm) ?? []).length;
  const numbered = (block.match(/^Reference image \d+ URL:/gm) ?? []).length;
  return single + numbered;
}

function refsSuffix(count: number): string {
  if (count <= 0) return "";
  return count === 1 ? " · 1 reference image" : ` · ${count} reference images`;
}

// ── Banan single ────────────────────────────────────────────────────────────

function summarizeBananSingleBrief(text: string): string | null {
  const promptMatch = text.match(/^Prompt:\s*(.+)$/m);
  if (!promptMatch) return null;
  const aspectMatch = text.match(/^Aspect ratio:\s*([^\n]+)$/m);
  const refCount = countRefUrls(text);

  const lines = [
    "**Image generation request**",
    "",
    `- **Prompt:** ${truncate(promptMatch[1])}`,
  ];
  if (aspectMatch) lines.push(`- **Aspect ratio:** ${aspectMatch[1].trim()}`);
  if (refCount > 0) {
    lines.push(`- **References:** ${refCount} attached`);
  }
  return lines.join("\n");
}

// ── Banan batch ─────────────────────────────────────────────────────────────

function summarizeBananBatchBrief(text: string): string | null {
  const countMatch = text.match(/^Generate (\d+) images? using the generate_image tool\./m);
  if (!countMatch) return null;
  const total = Number(countMatch[1]);

  const blocks = extractImageBlocks(text);
  if (blocks.length === 0) return null;

  const kept = blocks.slice(0, MAX_LIST_ITEMS);
  const remainder = blocks.length - kept.length;

  const lines: string[] = [`**${total} images requested**`, ""];
  kept.forEach((block) => {
    const refs = refsSuffix(block.refCount);
    lines.push(`${block.index}. ${truncate(block.prompt)}${refs}`);
  });
  if (remainder > 0) lines.push(`…and ${remainder} more`);

  const aspects = uniqueAspectRatios(blocks);
  if (aspects.length > 0) {
    lines.push("");
    lines.push(`Aspect ratio: ${aspects.join(", ")}`);
  }
  return lines.join("\n");
}

interface ImageBlock {
  index: number;
  prompt: string;
  aspect?: string;
  refCount: number;
}

function extractImageBlocks(text: string): ImageBlock[] {
  const blocks: ImageBlock[] = [];
  const headerRegex = /^Image (\d+):\s*$/gm;
  const headers: Array<{ index: number; offset: number; endOfLine: number }> = [];
  let match: RegExpExecArray | null;
  while ((match = headerRegex.exec(text)) !== null) {
    headers.push({
      index: Number(match[1]),
      offset: match.index,
      endOfLine: match.index + match[0].length,
    });
  }
  for (let i = 0; i < headers.length; i += 1) {
    const start = headers[i].endOfLine;
    const end = i + 1 < headers.length ? headers[i + 1].offset : text.length;
    const body = text.slice(start, end);
    const promptMatch = body.match(/^\s*Prompt:\s*(.+)$/m);
    if (!promptMatch) continue;
    const aspectMatch = body.match(/^Aspect ratio:\s*([^\n]+)$/m);
    blocks.push({
      index: headers[i].index,
      prompt: promptMatch[1],
      aspect: aspectMatch?.[1]?.trim(),
      refCount: countRefUrls(body),
    });
  }
  return blocks;
}

function uniqueAspectRatios(blocks: Array<{ aspect?: string }>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of blocks) {
    if (!b.aspect) continue;
    if (seen.has(b.aspect)) continue;
    seen.add(b.aspect);
    out.push(b.aspect);
  }
  return out;
}

// ── Image ad remix / brief (ImageAdSetupModal) ──────────────────────────────

/**
 * Collapse the image-ad dispatch brief built by `useAdDispatch.buildImageBrief`
 * down to a 4–5 line user-facing summary. The raw brief is ~80 lines of
 * generator instructions, PRESERVE/REPLACE blocks, brand DNA, and reference
 * URLs — none of which a user reading the chat needs to re-skim. Keep the
 * essentials: product, format, variant count, aspect ratio, and how many
 * references/product photos were attached.
 */
function summarizeImageAdBrief(text: string): string | null {
  const isRemix = text.startsWith("# Image Ad Remix");
  const productLine = text.match(/^(?:Product|- Product):\s*(.+)$/m)?.[1]?.trim();
  if (!productLine) return null;

  // Format line shape:
  //   Remix: `## Format\n<title> — <description>`
  //   From-scratch: `Format: <title> — <description>`
  const formatTitle =
    text.match(/^## Format\s*\n([^\n—-]+?)(?:\s*[—–-]\s*[^\n]+)?$/m)?.[1]?.trim() ??
    text.match(/^Format:\s*([^—–\n-]+?)(?:\s*[—–-]\s*[^\n]+)?$/m)?.[1]?.trim();

  // Variant count: "Generate N variant(s) — …" (remix) or "Variants: N" (from-scratch)
  const variantCount =
    Number(text.match(/Generate (\d+) variants?/m)?.[1]) ||
    Number(text.match(/^Variants:\s*(\d+)/m)?.[1]) ||
    0;

  // Aspect ratio: "## Aspect ratio\n<value>" (remix) or "Aspect ratio: <value>" (from-scratch)
  const aspect =
    text.match(/^## Aspect ratio\s*\n([^\n]+)$/m)?.[1]?.trim() ??
    text.match(/^Aspect ratio:\s*([^\n]+)$/m)?.[1]?.trim();

  // Count attached refs + product photos (bulleted/numbered URL lines under
  // the relevant section headers).
  const refCount = countSectionUrls(text, /^## Reference image[^\n]*\n/m);
  const productPhotoCount = countSectionUrls(text, /^## Product photos[^\n]*\n/m);

  const lines: string[] = [];
  lines.push(
    `**${isRemix ? "Image ad remix" : "Image ad"}${variantCount ? ` · ${variantCount} variant${variantCount === 1 ? "" : "s"}` : ""}**`,
  );
  lines.push("");
  lines.push(`- **Product:** ${truncate(productLine, 100)}`);
  if (formatTitle) lines.push(`- **Format:** ${truncate(formatTitle, 80)}`);
  if (aspect) lines.push(`- **Aspect ratio:** ${aspect}`);
  const attachParts: string[] = [];
  if (refCount > 0) attachParts.push(`${refCount} reference${refCount === 1 ? "" : "s"}`);
  if (productPhotoCount > 0)
    attachParts.push(`${productPhotoCount} product photo${productPhotoCount === 1 ? "" : "s"}`);
  if (attachParts.length > 0) lines.push(`- **Attached:** ${attachParts.join(" + ")}`);
  return lines.join("\n");
}

/** Count `- url` / `1. url` lines following a section header until the next
 *  blank line or `## ` header. Used to size attachments without listing the
 *  signed CDN URLs themselves. */
function countSectionUrls(text: string, headerRegex: RegExp): number {
  const headerMatch = text.match(headerRegex);
  if (!headerMatch || headerMatch.index === undefined) return 0;
  const start = headerMatch.index + headerMatch[0].length;
  const after = text.slice(start);
  // Stop at next blank line followed by another `## ` header, OR at end of text.
  const stopMatch = after.match(/\n\s*\n##\s|\n\s*\n[A-Z]/);
  const block = stopMatch ? after.slice(0, stopMatch.index) : after;
  const urlLines = block.match(/^\s*(?:[-*]|\d+\.)\s+https?:\/\/\S+/gm);
  return urlLines?.length ?? 0;
}

// ── UGC scene batch ─────────────────────────────────────────────────────────

function summarizeUgcSceneBrief(text: string): string | null {
  const headerMatch = text.match(/^# UGC Video (\d+) of (\d+) — Scene Reference Batch/m);
  if (!headerMatch) return null;

  const videoIndex = Number(headerMatch[1]);
  const totalVideos = Number(headerMatch[2]);

  const productLine = text.match(/^Product:\s*(.+)$/m)?.[1]?.trim();
  const hookType = text.match(/^Hook type:\s*(.+)$/m)?.[1]?.trim();
  const scriptAngle = text.match(/^Script angle:\s*(.+)$/m)?.[1]?.trim();
  const creator = text.match(/^Creator persona:\s*(.+)$/m)?.[1]?.trim();

  const scenes = extractUgcScenes(text);

  const lines: string[] = [
    `**UGC scene references — Video ${videoIndex} of ${totalVideos}**`,
    "",
  ];
  if (productLine) lines.push(`- **Product:** ${truncate(productLine, 120)}`);
  if (hookType) lines.push(`- **Hook:** ${hookType}`);
  if (scriptAngle) lines.push(`- **Script angle:** ${truncate(scriptAngle, 140)}`);
  if (creator) lines.push(`- **Creator:** ${creator}`);

  if (scenes.length > 0) {
    lines.push("");
    lines.push(`**${scenes.length} storyboard frames**`);
    scenes.forEach((scene) => {
      const label = scene.slot ? ` **${scene.slot}**:` : "";
      lines.push(`${scene.index}.${label} ${truncate(scene.prompt)}`);
    });
  }

  return lines.join("\n");
}

interface UgcScene {
  index: number;
  total: number;
  slot?: string;
  prompt: string;
}

function extractUgcScenes(text: string): UgcScene[] {
  const out: UgcScene[] = [];
  const headerRegex = /^## Scene (\d+) of (\d+)(?:\s*—\s*([^\n]+))?$/gm;
  const headers: Array<{
    index: number;
    total: number;
    slot?: string;
    offset: number;
    endOfLine: number;
  }> = [];
  let match: RegExpExecArray | null;
  while ((match = headerRegex.exec(text)) !== null) {
    headers.push({
      index: Number(match[1]),
      total: Number(match[2]),
      slot: match[3]?.trim(),
      offset: match.index,
      endOfLine: match.index + match[0].length,
    });
  }
  for (let i = 0; i < headers.length; i += 1) {
    const start = headers[i].endOfLine;
    const end = i + 1 < headers.length ? headers[i + 1].offset : text.length;
    const body = text.slice(start, end);
    const promptMatch = body.match(/^\s*Prompt:\s*(.+)$/m);
    if (!promptMatch) continue;
    out.push({
      index: headers[i].index,
      total: headers[i].total,
      slot: headers[i].slot,
      prompt: promptMatch[1],
    });
  }
  return out;
}

// ── UGC element stills brief ────────────────────────────────────────────────

function summarizeUgcElementStillsBrief(text: string): string | null {
  const headerMatch = text.match(/^# UGC Element Stills — Video (\d+) of (\d+)/m);
  if (!headerMatch) return null;

  const videoIndex = Number(headerMatch[1]);
  const totalVideos = Number(headerMatch[2]);
  const productLine = text.match(/^Product:\s*(.+)$/m)?.[1]?.trim();
  const scriptAngle = text.match(/^Script angle:\s*(.+)$/m)?.[1]?.trim();
  const genLine = text.match(
    /^Generating:\s*(\d+) element still\(s\) \+ (\d+) text frame\(s\) = (\d+) image\(s\) total$/m,
  );

  const blocks = extractImageBlocks(text);

  const lines: string[] = [
    `**UGC element stills — Video ${videoIndex} of ${totalVideos}**`,
    "",
  ];
  if (productLine) lines.push(`- **Product:** ${truncate(productLine, 120)}`);
  if (scriptAngle) lines.push(`- **Script angle:** ${truncate(scriptAngle, 140)}`);
  if (genLine) {
    const elementN = Number(genLine[1]);
    const textN = Number(genLine[2]);
    const totalN = Number(genLine[3]);
    const breakdown = [
      elementN > 0 ? `${elementN} element still${elementN === 1 ? "" : "s"}` : null,
      textN > 0 ? `${textN} text frame${textN === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    lines.push(
      `- **${totalN} image${totalN === 1 ? "" : "s"} requested**${breakdown.length ? ` — ${breakdown.join(" + ")}` : ""}`,
    );
  }

  if (blocks.length > 0) {
    const kept = blocks.slice(0, MAX_LIST_ITEMS);
    const remainder = blocks.length - kept.length;
    lines.push("");
    kept.forEach((block) => {
      const { label, description } = extractElementLabel(block.prompt);
      const prefix = label ? `**${label}** — ` : "";
      const body = truncate(description || block.prompt);
      lines.push(`${block.index}. ${prefix}${body}`);
    });
    if (remainder > 0) lines.push(`…and ${remainder} more`);
  }

  return lines.join("\n");
}

/** UGC-element-still prompts pack the element name into the body as
 *  `... Element: <name>. Description: <text>. ...`. Pull the name out as a
 *  label and return just the human-friendly description so the list item
 *  reads like `1. **Bright Bathroom** — bright bathroom vanity counter...`
 *  instead of the raw 200-char prompt. Returns nulls when the markers aren't
 *  present so the caller can fall back to the raw prompt. */
function extractElementLabel(prompt: string): { label?: string; description?: string } {
  const labelMatch = prompt.match(/Element:\s*([^.]+?)\.\s*/);
  const descMatch = prompt.match(/Description:\s*(.+)$/);
  return {
    label: labelMatch?.[1]?.trim(),
    description: descMatch?.[1]?.trim(),
  };
}

// ── Kling JSON brief ────────────────────────────────────────────────────────

function summarizeKlingBrief(text: string): string | null {
  const fenceMatch = text.match(/```json\s*\n([\s\S]*?)\n\s*```/);
  if (!fenceMatch) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(fenceMatch[1]);
  } catch {
    return "**Video generation request**\n\n_(brief details hidden)_";
  }

  const record = asRecord(parsed);
  const items = Array.isArray(record.videos)
    ? (record.videos as unknown[])
    : Array.isArray(record.images)
      ? (record.images as unknown[])
      : [];
  if (items.length === 0) return null;

  const isImage = Array.isArray(record.images);
  const itemNoun = isImage ? "image" : "video";
  const plural = items.length === 1 ? itemNoun : `${itemNoun}s`;

  const summaries = items.slice(0, MAX_LIST_ITEMS).map((item) => summarizeKlingItem(item));
  const remainder = items.length - summaries.length;

  const lines: string[] = [`**${items.length} ${plural} requested**`, ""];

  const productName = summaries.find((s) => s.product)?.product;
  if (productName) lines.push(`- **Product:** ${productName}`);

  if (items.length === 1) {
    const s = summaries[0];
    if (s.mode) lines.push(`- **Mode:** ${s.mode}`);
    if (s.aspect) lines.push(`- **Aspect ratio:** ${s.aspect}`);
    if (s.duration) lines.push(`- **Duration:** ${s.duration}s`);
    if (s.hookType) lines.push(`- **Hook:** ${s.hookType}`);
    if (s.angle) lines.push(`- **Angle:** ${truncate(s.angle, 140)}`);
    if (s.beats) lines.push(`- **Script beats:** ${s.beats}`);
    if (s.promptSnippet) {
      lines.push("");
      lines.push(`> ${truncate(s.promptSnippet, 200)}`);
    }
    return lines.join("\n");
  }

  const cols: string[] = ["#"];
  const pick = {
    mode: summaries.some((s) => s.mode),
    aspect: summaries.some((s) => s.aspect),
    duration: summaries.some((s) => s.duration),
    hook: summaries.some((s) => s.hookType),
    angle: summaries.some((s) => s.angle),
  };
  if (pick.mode) cols.push("Mode");
  if (pick.aspect) cols.push("Aspect");
  if (pick.duration) cols.push("Duration");
  if (pick.hook) cols.push("Hook");
  if (pick.angle) cols.push("Angle");

  lines.push("");
  lines.push(`| ${cols.join(" | ")} |`);
  lines.push(`| ${cols.map(() => "---").join(" | ")} |`);
  summaries.forEach((s, i) => {
    const row = [String(i + 1)];
    if (pick.mode) row.push(s.mode ?? "—");
    if (pick.aspect) row.push(s.aspect ?? "—");
    if (pick.duration) row.push(s.duration ? `${s.duration}s` : "—");
    if (pick.hook) row.push(s.hookType ?? "—");
    if (pick.angle) row.push(s.angle ? truncate(s.angle, 60) : "—");
    lines.push(`| ${row.join(" | ")} |`);
  });
  if (remainder > 0) {
    lines.push("");
    lines.push(`…and ${remainder} more`);
  }

  return lines.join("\n");
}

interface KlingItemSummary {
  mode?: string;
  product?: string;
  aspect?: string;
  duration?: string;
  hookType?: string;
  angle?: string;
  beats?: number;
  promptSnippet?: string;
}

function summarizeKlingItem(raw: unknown): KlingItemSummary {
  const item = asRecord(raw);
  const product = asRecord(item.product);
  const direction = asRecord(item.direction);
  const narrative = asRecord(direction.narrative);
  const script = asRecord(direction.script);
  const constraints = asRecord(item.constraints);
  const beatsArr = Array.isArray(script.beats) ? (script.beats as unknown[]) : undefined;

  return {
    mode: asString(item.mode),
    product: asString(product.name),
    aspect: asString(constraints.aspectRatio),
    duration: normalizeDuration(constraints.duration),
    hookType: asString(script.hookType) ?? asString(narrative.hookLine),
    angle: asString(script.angle) ?? asString(narrative.angle),
    beats: beatsArr?.length,
    promptSnippet: asString(direction.prompt),
  };
}

function normalizeDuration(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

// ── Google Ads brief ────────────────────────────────────────────────────────

/**
 * Collapse the agent-facing Google Ads brief (built by `buildGadsBrief` in
 * core) down to a compact, human-scannable summary. The raw brief carries
 * the full tool-registry instructions, every seed keyword, ad-copy guard
 * rails, and a `### How to operate` block — none of which a user needs to
 * re-read in the chat transcript. Keep: goal, product, budget, channel,
 * landing URL, and the original natural-language ask.
 */
function summarizeGadsBrief(text: string): string | null {
  const goalType = text.match(/^\*\*Type:\*\*\s*(.+)$/m)?.[1]?.trim();
  if (!goalType) return null;

  const userRequest = text.match(/\*\*User request:\*\*\s*\n([\s\S]*?)(?:\n### |\n*$)/)?.[1]?.trim();
  const productName = text.match(/^- \*\*Product:\*\*\s*(.+)$/m)?.[1]?.trim();
  const channelType = text.match(/^- \*\*Channel type:\*\*\s*(.+)$/m)?.[1]?.trim();
  const dailyBudget = text.match(/^- \*\*Daily budget:\*\*\s*([\d.]+)/m)?.[1]?.trim();
  const landingUrl = text.match(/^- \*\*Landing page:\*\*\s*(\S+)/m)?.[1]?.trim();
  const keywordCount = (text.match(/^- [^*\n]/gm) ?? []).length;

  const lines: string[] = ["**Google Ads task**", ""];
  lines.push(`- **Goal:** ${goalType}`);
  if (productName) lines.push(`- **Product:** ${truncate(productName, 80)}`);
  if (channelType) lines.push(`- **Channel:** ${channelType}`);
  if (dailyBudget) lines.push(`- **Daily budget:** $${dailyBudget}`);
  if (landingUrl) {
    try {
      const host = new URL(landingUrl).hostname;
      lines.push(`- **Landing:** ${host}`);
    } catch {
      lines.push(`- **Landing:** ${truncate(landingUrl, 60)}`);
    }
  }
  if (keywordCount > 0) lines.push(`- **Seed items:** ${keywordCount}`);

  if (userRequest) {
    lines.push("");
    lines.push(`> ${truncate(userRequest, 220)}`);
  }

  return lines.join("\n");
}
